import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ColumnModel } from '@shared/components/beyond-table/model/column.model';
import { MatOptionSelectionChange } from '@angular/material/core';
import { DevExtremeFieldTypesEnum } from '@views/secured/beyond-smart-reports/enums/dev-extreme-field-types.enum';
import {
  SummaryTypeEnum
} from '@views/secured/beyond-smart-reports/views/report/components/devextreme/summaries-dialog/enum/summary-type.enum';
import {
  SummaryFilterModel
} from '@views/secured/beyond-smart-reports/views/report/components/devextreme/summaries-dialog/model/summary-filter.model';
import { markAllAsTouched } from '@shared/util/form.util';
import { SnackBarService } from '@base/services/snackbar.service';
import {
  DialogTemplateButtonComboStyleEnum
} from '@shared/components/templates/dialog-template/enums/dialog-template-button.enum';
import { CustomFieldModel } from '@base/modules/rest/custom-field/model/custom-field.model';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

interface DialogFormArrayModel {
  columnType: FormControl<DevExtremeFieldTypesEnum>;
  columnName: FormControl<string>;
  summaryType: FormControl<SummaryTypeEnum>;
}

interface DialogFormModel {
  summaryInputs: FormArray<FormGroup<DialogFormArrayModel>>;
}

interface SummariesDialogData {
  columns: ColumnModel[];
  summaryFilters: SummaryFilterModel[];
  fields?: CustomFieldModel[];
}

interface OptionModel<A = string | number | [], B = string, C = any> {
  value: A;
  data: B;
  dataType?: C;
}

@Component({
  selector: 'app-summaries-dialog',
  templateUrl: './summaries-dialog.component.html',
  styleUrls: ['./summaries-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class SummariesDialogComponent {

  form: FormGroup<DialogFormModel>;
  columnsData: OptionModel[] = [];
  fieldsCleared = false;
  DialogTemplateButtonComboStyleEnum = DialogTemplateButtonComboStyleEnum;

  constructor(@Inject(MAT_DIALOG_DATA) public data: SummariesDialogData,
              private dialogRef: MatDialogRef<SummariesDialogComponent>,
              private snackBarService: SnackBarService) {
    this.form = new FormGroup<DialogFormModel>({
      summaryInputs: new FormArray<FormGroup<DialogFormArrayModel>>([]),
    });

    this.columnsData = this.data.columns.map((column: ColumnModel) => {
      return {
        value: column.dataField,
        data: data?.fields ? column.caption : column.dataField,
        dataType: column.dataType,
      };
    });
    if (this.data.summaryFilters.length > 0) {
      this.data.summaryFilters?.map((summaryFilter: SummaryFilterModel) => {
        const group = new FormGroup<DialogFormArrayModel>({
          columnType: new FormControl<DevExtremeFieldTypesEnum>(summaryFilter.columnType),
          columnName: new FormControl<string>(summaryFilter.columnName, Validators.required),
          summaryType: new FormControl<SummaryTypeEnum>(summaryFilter.summaryType, Validators.required),
        });
        this.summaryInputs.push(group);
      });
    } else {
      this.generateEmptyGroups();
    }
  }

  generateEmptyGroups(): void {
    const summaryGroup = new FormGroup<DialogFormArrayModel>({
      columnType: new FormControl<DevExtremeFieldTypesEnum>(null),
      columnName: new FormControl<string>(null, Validators.required),
      summaryType: new FormControl<SummaryTypeEnum>(null, Validators.required),
    });
    this.summaryInputs.push(summaryGroup);
  }

  get summaryInputs(): FormArray<FormGroup<DialogFormArrayModel>> {
    return this.form.get('summaryInputs') as FormArray<FormGroup<DialogFormArrayModel>>;
  }

  get summaryGroups(): FormGroup<DialogFormArrayModel>[] {
    return this.summaryInputs.controls;
  }

  static open(dialog: MatDialog, data: SummariesDialogData): MatDialogRef<SummariesDialogComponent, SummaryFilterModel[]> {
    return dialog.open(SummariesDialogComponent, {
      data,
      width: '1000px',
    });
  }

  closeDialog(): void {
    this.dialogRef.close(this.data.summaryFilters);
  }

  deleteSummaryGroup(i: number): void {
    this.summaryInputs.removeAt(i);
  }

  addSummaryGroup(): void {
    this.generateEmptyGroups();
  }

  clearFilters(): SummaryFilterModel[] {
    let filters: SummaryFilterModel[] = [];
    this.summaryGroups.forEach((summaryGroup: FormGroup<DialogFormArrayModel>) => {
      summaryGroup.controls.columnName.setValue(null);
      summaryGroup.controls.columnType.setValue(null);
      summaryGroup.controls.summaryType.setValue(null);
      const item: SummaryFilterModel = {
        columnName: null,
        columnType: null,
        summaryType: null,
      };
      filters = [...filters, item];
    });
    this.summaryGroups.length = 1;
    this.fieldsCleared = true;
    return filters;
  }

  getFilterValues(): SummaryFilterModel[] {
    let filters: SummaryFilterModel[] = [];
    this.summaryGroups
      .forEach((summary: FormGroup<DialogFormArrayModel>) => {
        const item: SummaryFilterModel = {
          columnName: summary.getRawValue().columnName,
          columnType: summary.getRawValue().columnType,
          summaryType: summary.getRawValue().summaryType,
        };
        filters = [...filters, item];
      });
    return filters;
  }

  onColumnNameSelectionChanged(event: MatAutocompleteSelectedEvent, formGroup: FormGroup<DialogFormArrayModel>): void {
    const field = this.columnsData.find(column => {
      // if (!event.isUserInput) {
      //   return null;
      // }
      return column.value === event.option.value;
    });

    if (field) {
      formGroup.controls.columnType.setValue(field.dataType);
      formGroup.controls.summaryType.enable();
    }
  }

  applySummaries(): void {
    if (!this.fieldsCleared) {
      if (!this.form.valid) {
        markAllAsTouched(this.form);
        this.snackBarService.openErrorMessage('COMMON.INVALID_FORM');
        return;
      }
    }
    const summaryFilters = this.getFilterValues();
    this.dialogRef.close(summaryFilters);
  }

  getSelectOptions(index: number): OptionModel[] {
    const otherItemNames = this.summaryInputs.controls
      .filter((item, arrayIndex) => arrayIndex !== index);

    return this.columnsData
      .filter(option => {
        return !otherItemNames.some(otherItem => option.value === otherItem.value.columnName);
      });
  }
}
